import { useState, useCallback } from 'react'
import { useMediaQuery } from 'react-responsive'
import useDebouncedCallback from '../../../../hooks/use-debounced-callback'
import { FormField, Select } from '../../../../components/inputs'
import { Button } from '../../../../components/button'
import useManualEntryOptions from './use-manual-entry-options'

export default function NotFound({ onSubmit }) {
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' })
  const [carDetails, setCarDetails] = useState({
    vin: null,
    year: null,
    make: null,
    model: null,
    shortTrimName: null,
  })

  const handleVinChange = useCallback((event) => {
    const { value } = event.target
    setCarDetails((prev) => ({ ...prev, vin: value }))
  }, [])

  const handleYearChange = useDebouncedCallback((value) => {
    setCarDetails((prev) => ({
      ...prev,
      year: +value,
      make: null,
      model: null,
      shortTrimName: null,
    }))
  }, 500)

  const handleSelectChange = useCallback((newValue, { name }) => {
    if (name === 'make') {
      return setCarDetails((prev) => ({
        ...prev,
        model: null,
        shortTrimName: null,
        [name]: newValue,
      }))
    }

    if (name === 'model') {
      return setCarDetails((prev) => ({ ...prev, shortTrimName: null, [name]: newValue }))
    }

    setCarDetails((prev) => ({ ...prev, [name]: newValue }))
  }, [])

  const {
    makeOptions,
    isMakesFetching,
    modelOptions,
    isModelsFetching,
    shortTrimOptions,
    isShortTrimsFetching,
  } = useManualEntryOptions({
    year: carDetails.year,
    makeId: carDetails.make?.value,
    modelId: carDetails.model?.value,
  })

  const handleSubmit = (event) => {
    event.preventDefault()

    onSubmit({
      vin: carDetails.vin,
      year: carDetails.year,
      modelId: carDetails.model?.value,
      modelName: carDetails.model?.label,
      makeId: carDetails.make?.value,
      makeName: carDetails.make?.label,
      shortTrimName: carDetails.shortTrimName?.value,
    })
  }

  const continueBtnDisabled = Object.keys(carDetails).some((key) => carDetails[key] == null)
  console.log(carDetails)

  return (
    <div className='notFoundView'>
      <h1 className='title'>We couldn’t find your vehicle</h1>
      <p className='description'>If your VIN look correct please fill out the fields below</p>
      <form onSubmit={handleSubmit}>
        <div className='inputContainer vin'>
          <FormField aria-label='vin' name='vin' placeholder='Vin' onChange={handleVinChange} />
        </div>
        <div className='inputContainer'>
          <FormField
            aria-label='year'
            type='tel'
            name='year'
            placeholder='Year'
            onChange={(e) => handleYearChange(e.target.value)}
          />
        </div>
        <div className='inputContainer'>
          <Select
            isLoading={isMakesFetching}
            aria-label='make'
            name='make'
            placeholder='Make'
            value={carDetails.make}
            options={makeOptions}
            menuPlacement={isMobile ? 'top' : 'bottom'}
            maxMenuHeight={200}
            onChange={handleSelectChange}
          />
        </div>
        <div className='inputContainer'>
          <Select
            isLoading={isModelsFetching}
            aria-label='model'
            name='model'
            placeholder='Model'
            value={carDetails.model}
            options={modelOptions}
            menuPlacement={isMobile ? 'top' : 'bottom'}
            maxMenuHeight={150}
            onChange={handleSelectChange}
          />
        </div>
        <div className='inputContainer'>
          <Select
            isLoading={isShortTrimsFetching}
            aria-label='shortTrimName'
            name='shortTrimName'
            menuPlacement='top'
            placeholder='Trim'
            value={carDetails.shortTrimName}
            options={shortTrimOptions}
            maxMenuHeight={200}
            onChange={handleSelectChange}
          />
        </div>
        <div className='buttonsContainer'>
          <Button type='submit' disabled={continueBtnDisabled}>
            Continue
          </Button>
        </div>
      </form>

      <style jsx>{`
        .notFoundView {
          max-width: 100vw;
          width: 43.6rem;
          background: var(--primary-bg-color);
        }

        .title {
          max-width: 30rem;
          width: 100%;
          margin: 0 auto;
          padding: 3.4rem 0 0;
          text-align: center;
          font-size: 3.4rem;
        }

        .description {
          margin: 1.7rem 0 0;
          text-align: center;
          font-size: 1.6rem;
        }

        form {
          max-width: 39.7rem;
          width: 100%;
          margin: 0 auto;
          padding: 1.6rem 0 4.1rem;
        }

        .inputContainer {
          margin: 0 0 2.4rem;

          &.vin {
            margin: 0 0 3.1rem;
          }
        }

        .buttonsContainer {
          margin: 0.8rem 0 0;
          text-align: center;
        }

        @media (max-width: 1024px) {
          .notFoundView {
            max-width: 34.7rem;
            width: 100%;
            margin: 0 auto;
            height: 100%;
          }

          .title {
            padding: 6rem 0 0;
            font-size: 1.8rem;
          }

          .description {
            font-size: 1.4rem;
          }

          form {
            max-width: 100%;
            padding: 2.6rem 0;
          }

          .buttonsContainer {
            position: fixed;
            left: 0;
            bottom: 2.1rem;
            width: 100%;
            padding: 0 1.2rem;

            :global(button) {
              width: 100%;
            }
          }
        }
      `}</style>
    </div>
  )
}
